import {
    FilterSelection,
    FullBleed,
    HorizontalDivider,
    IconLocalShipping,
    IconLocationOn,
    IconShoppingBag,
    Stack,
    Tabs,
} from '@phx/design-system';
import { useDisclosure, useIsMobile } from '@phx/design-system/hooks';
import { useTelemetryContext } from '@phx/instrumentation/client';
import { useGeolocation } from '@phx/location-utils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import {
    type ProviderOffersInfoFragment,
    SuggestedProviderOffersReason,
} from '../../graphql/generated/graphql';
import { useNumericSearchParam } from '../../hooks/use-numeric-search-param';
import { getAbsoluteRoute } from '../../util';
import {
    sortOffersByDistance,
    sortOffersByPrice,
} from '../../util/offers/sort-offers';
import { InfoHeading } from '../common/InfoHeading';
import { LocationPickerCover } from '../location-picker-cover';

import { AlternativesLink } from './alternatives-link/AlternativesLink';
import { ProviderOffersCard, ProviderOffersCardList } from './cards';
import { NoLocation } from './cards/NoLocation';

type ProviderSelectionProps = {
    header: React.ReactNode;
    providerOffersByType: {
        delivery: ProviderOffersInfoFragment[];
        pickup: ProviderOffersInfoFragment[];
    };
    onSelect: (providerOffersId: string) => void;
    featuredProvider?: {
        providerOffers: ProviderOffersInfoFragment;
        reason: SuggestedProviderOffersReason;
    };
};

export const ProviderSelection = ({
    header,
    providerOffersByType,
    onSelect,
    featuredProvider,
}: ProviderSelectionProps) => {
    const { telemetryInstance } = useTelemetryContext();

    const { t } = useTranslation();
    const { geolocation } = useGeolocation();
    const [opened, { open, close }] = useDisclosure(false);
    const navigate = useNavigate();
    const isMobile = useIsMobile('md');

    const [
        pickupFiltersOpened,
        { open: openPickupFilters, close: closePickupFilters },
    ] = useDisclosure(false);
    const [
        deliveryFiltersOpened,
        { open: openDeliveryFilters, close: closeDeliveryFilters },
    ] = useDisclosure(false);

    const distance = useNumericSearchParam('within');
    const { prescriptionId } = useParams();

    const handleAltsClick = () => {
        if (prescriptionId && featuredProvider) {
            navigate(
                getAbsoluteRoute('cabinet.rxId.alternatives', {
                    prescriptionId,
                }) + `?distance=${distance}`
            );
        }
    };

    return (
        <Stack gap="lg">
            <Stack gap="sm">
                {header}
                <AlternativesLink
                    onAltsClick={handleAltsClick}
                    cheapestOffers={featuredProvider?.providerOffers}
                />
            </Stack>
            {header && <HorizontalDivider />}
            <Stack gap={0}>
                <InfoHeading
                    title={t('pickYourPharmacy.title')}
                    informationDialog={{
                        title: t('pickYourPharmacy.infoDialog.title'),
                        content: t('pickYourPharmacy.infoDialog.body'),
                    }}
                />
            </Stack>

            <Stack gap="lg" role="list">
                <LocationPickerCover isOpen={opened} onClose={close} />
                <FilterSelection
                    inputTestId="pick-your-pharmacy-location-picker-button"
                    input={
                        geolocation?.name ??
                        t('pickYourPharmacy.location.setLocationAndDistance')
                    }
                    StartIcon={IconLocationOn}
                    onInputClick={open}
                    distance={
                        geolocation?.name
                            ? `${distance} ${t('common.distance.distanceUnit')}`
                            : undefined
                    }
                />
                {featuredProvider && (
                    <ProviderOffersCard
                        role="listitem"
                        suggestedReasons={[featuredProvider.reason]}
                        providerOffers={featuredProvider.providerOffers}
                        onSelected={() =>
                            onSelect(featuredProvider.providerOffers.id)
                        }
                    />
                )}
            </Stack>

            {geolocation ? (
                <Tabs
                    defaultValue="pickup"
                    grow
                    onChange={(value: string | null) => {
                        value &&
                            telemetryInstance.logEvent({
                                name: `Provider select view toggle, ${value}`,
                                source: 'ProviderSelection',
                            });
                    }}
                    tabBarProps={{
                        fullBleed: isMobile,
                        sticky: true,
                    }}
                    tabs={[
                        {
                            label: t('pickYourPharmacy.pickUp'),
                            panel: (
                                <FullBleed breakpoint="md" color="gray">
                                    <ProviderOffersCardList
                                        providerOffers={sortOffersByDistance(
                                            providerOffersByType.pickup
                                        )}
                                        onSelect={onSelect}
                                        pickUp
                                        filterOpen={pickupFiltersOpened}
                                        openFilter={openPickupFilters}
                                        closeFilter={closePickupFilters}
                                    />
                                </FullBleed>
                            ),
                            value: 'pickup',
                            LeftIcon: IconShoppingBag,
                        },
                        {
                            label: t('pickYourPharmacy.delivery'),
                            panel: (
                                <FullBleed breakpoint="md" color="gray">
                                    <ProviderOffersCardList
                                        providerOffers={sortOffersByPrice(
                                            providerOffersByType.delivery
                                        )}
                                        onSelect={onSelect}
                                        filterOpen={deliveryFiltersOpened}
                                        openFilter={openDeliveryFilters}
                                        closeFilter={closeDeliveryFilters}
                                    />
                                </FullBleed>
                            ),
                            value: 'delivery',
                            LeftIcon: IconLocalShipping,
                        },
                    ]}
                />
            ) : (
                <NoLocation />
            )}
        </Stack>
    );
};
